import ExcelJs from 'exceljs'
import { Share } from '@capacitor/share'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'
import { WalletType } from '@/views/CreateWallet'

const MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const blobToBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onloadend = () => resolve((reader.result as string).split(',')[1])
  reader.onerror = reject
  reader.readAsDataURL(blob)
})

const createWorkbook = (data: WalletType[]) => {
  const workbook = new ExcelJs.Workbook()
  const sheet = workbook.addWorksheet('wallets.xlsx', { views: [{ showGridLines: true }] })
  const keys = Object.keys(data[0])
  sheet.addTable({
    name: 'myExce', ref: 'A1', headerRow: true, totalsRow: false,
    style: { showRowStripes: false },
    columns: keys.map((name) => ({ name })),
    rows: data.map((wallet) => keys.map((key) => wallet[key])),
  })
  sheet.columns = sheet.columns.map((column, index) => ({ width: [50, 100, 80][index] || 50 }))
  const table: any = sheet.getTable('myExce')
  for (let index = 0; index < table.table.columns.length; index += 1) {
    const cell = sheet.getCell(`${String.fromCharCode(65 + index)}1`)
    cell.font = { size: 18 }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'c5d9f1' } }
    for (let row = 0; row < table.table.rows.length; row += 1) {
      sheet.getCell(`${String.fromCharCode(65 + index)}${row + 1}`).alignment = { wrapText: true }
    }
  }
  table.commit()
  return workbook
}

const downloadInBrowser = (buffer: ExcelJs.Buffer, fileName: string) => {
  const url = URL.createObjectURL(new Blob([buffer], { type: MIME_TYPE }))
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.rel = 'noopener'
  link.click()
  // 给 WebView 的下载拦截器留出读取 blob 的时间。
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

const shareOnMobileWeb = async (buffer: ExcelJs.Buffer, fileName: string) => {
  const file = new File([buffer], fileName, { type: MIME_TYPE })
  const canShareFile = typeof navigator.share === 'function' &&
    (!navigator.canShare || navigator.canShare({ files: [file] }))

  if (canShareFile) {
    await navigator.share({
      title: '导出钱包表格',
      text: '请选择保存位置或使用表格应用打开',
      files: [file],
    })
    return true
  }

  return false
}

const exportToExcel = async (data: WalletType[]) => {
  if (!data.length) return
  const fileName = `wallets_${Date.now()}.xlsx`
  const buffer = await createWorkbook(data).xlsx.writeBuffer()
  const platform = Capacitor.getPlatform()

  if (Capacitor.isNativePlatform() && platform === 'android') {
    try {
      const base64 = await blobToBase64(new Blob([buffer], { type: MIME_TYPE }))
      let file
      try {
        file = await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Cache })
      } catch {
        file = await Filesystem.writeFile({ path: fileName, data: base64, directory: Directory.Documents })
      }
      await Share.share({
        title: '导出钱包表格',
        text: '请选择“保存到文件”或使用表格应用打开',
        files: [file.uri],
        dialogTitle: '保存钱包表格',
      })
    } catch (error) {
      if (!String(error).toLowerCase().includes('cancel')) {
        console.error('Android export failed:', error)
        alert(`导出失败：${String(error)}`)
      }
    }
    return
  }

  // TP 等钱包 DApp 浏览器属于 WebView，没有 Capacitor 原生文件系统。
  // 优先使用 Web Share 文件接口，避免把 blob URL 交给外部浏览器后失效。
  const isMobileWebView = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
  if (isMobileWebView) {
    try {
      if (await shareOnMobileWeb(buffer, fileName)) return
    } catch (error) {
      // 用户关闭分享面板时不再弹出错误，继续尝试 WebView 下载拦截器。
      if (!String(error).toLowerCase().includes('abort') && !String(error).toLowerCase().includes('cancel')) {
        console.warn('Mobile web share failed, fallback to download:', error)
      }
    }
  }

  downloadInBrowser(buffer, fileName)
}

export default exportToExcel
